"use client"

import { createContext, useContext, useState, useEffect, type ReactNode } from "react"

type User = {
  id: string
  name: string
  email: string
  plan: "free" | "premium"
  scansLeft: number
  subscriptionEndsAt?: string
}

type AuthContextType = {
  user: User | null
  isLoading: boolean
  isAnonymous: boolean
  setIsAnonymous: (value: boolean) => void
  login: (email: string, password: string) => Promise<void>
  signup: (name: string, email: string, password: string) => Promise<void>
  logout: () => void
  resetPassword: (email: string) => Promise<void>
  updateUser: (data: Partial<User>) => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isAnonymous, setIsAnonymous] = useState(false)

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem("token")

    if (token) {
      fetchUserProfile(token)
    } else {
      setIsLoading(false)
      setIsAnonymous(true)
    }
  }, [])

  const fetchUserProfile = async (token: string) => {
    try {
      const response = await fetch("/api/user", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const userData = await response.json()
        setUser(userData)
        setIsAnonymous(false)
      } else {
        // Token is invalid
        localStorage.removeItem("token")
        setIsAnonymous(true)
      }
    } catch (error) {
      console.error("Error fetching user profile:", error)
      setIsAnonymous(true)
    } finally {
      setIsLoading(false)
    }
  }

  const login = async (email: string, password: string) => {
    setIsLoading(true)

    try {
      const response = await fetch("/api/auth", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "login",
          email,
          password,
        }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || "Login failed")
      }

      const { user, token } = await response.json()

      // Save token
      localStorage.setItem("token", token)

      // Set user
      setUser(user)
      setIsAnonymous(false)
    } catch (error) {
      console.error("Login error:", error)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const signup = async (name: string, email: string, password: string) => {
    setIsLoading(true)

    try {
      const response = await fetch("/api/auth", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "register",
          name,
          email,
          password,
        }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || "Signup failed")
      }

      const { user, token } = await response.json()

      // Save token
      localStorage.setItem("token", token)

      // Set user
      setUser(user)
      setIsAnonymous(false)
    } catch (error) {
      console.error("Signup error:", error)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const logout = () => {
    localStorage.removeItem("token")
    setUser(null)
    setIsAnonymous(true)
  }

  const resetPassword = async (email: string) => {
    setIsLoading(true)

    try {
      const response = await fetch("/api/auth", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "reset-password",
          email,
        }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || "Password reset failed")
      }
    } catch (error) {
      console.error("Password reset error:", error)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const updateUser = async (data: Partial<User>) => {
    if (!user) return

    try {
      const token = localStorage.getItem("token")

      if (!token) {
        throw new Error("Not authenticated")
      }

      const response = await fetch("/api/user", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || "Update failed")
      }

      const updatedUser = await response.json()
      setUser(updatedUser)
    } catch (error) {
      console.error("Update user error:", error)
      throw error
    }
  }

  return (
    

    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAnonymous,
        setIsAnonymous,
        login,
        signup,
        logout,
        resetPassword,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}

